import { useEffect, useLayoutEffect, useState } from 'react'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, ChevronLeft, ChevronRight, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { NeedsYouStack } from '@/components/today/NeedsYouStack'
import { ThreadChat } from '@/components/dashboard/v2/ThreadChat'
import { useShell } from '@/components/shell/shell-context'
import { authFetch } from '@/lib/api'
import { formatRelative, formatShortWhen } from '@/lib/dates'
import {
  commitDigestStamp,
  countEntriesSince,
  peekDigestBaseline,
  type DigestEntry,
} from '@/lib/today-digest'
import { useAssistantName } from '@/lib/use-assistant-name'
import {
  canClearThread,
  closeThread,
  fetchRail,
  markThreadClosedInRail,
  pickAutoReopenThread,
  threadKeys,
  visibleRailThreads,
  type ThreadRailItem,
} from '@/lib/threads'

/**
 * Today — the tenant home (DESIGN.md §5, plan PR-5). Replaces the
 * Dashboard component at `/`: needs-you stack (≤3 amber cards), the
 * one-line "While you were working" digest, and the thread rail. The
 * composer is the shell-level CommandDock (§4.3) — nothing mounts here.
 * Home stays quiet: the durable log lives under More → Activity (PR-7).
 *
 * The thread-workspace machinery (deep link, auto-reopen latch,
 * close/clear cache repair, dock suppression) carries over from HomeV2
 * verbatim — HomeV2/AgenticDashboard stay untouched until the cleanup
 * PR deletes them.
 */
export function Today() {
  const { getToken, isLoaded } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    searchParams.get('thread'),
  )
  const [evaporatingId, setEvaporatingId] = useState<string | null>(null)

  // A ?thread=ID deep-link (from the Threads archive) opens that thread,
  // then clears the param so a later refresh/back doesn't re-open it.
  const threadParam = searchParams.get('thread')
  // Select in render on param CHANGE (canonical prev-snapshot pattern): a
  // fresh mount is covered by the useState initializer above, but a
  // same-route arrival — the dock voice handoff navigating /?thread=X
  // while Today is already mounted — never re-runs an initializer, and
  // without this the param would be stripped with nothing selected.
  const [prevThreadParam, setPrevThreadParam] = useState(threadParam)
  if (threadParam !== prevThreadParam) {
    setPrevThreadParam(threadParam)
    if (threadParam) setSelectedId(threadParam)
  }
  useEffect(() => {
    if (!threadParam) return
    // selectedId is seeded by the useState initializer (fresh mount) or the
    // prev-snapshot block above (same-route arrival); the effect's only job
    // is stripping the consumed param (History API sync).
    const next = new URLSearchParams(searchParams)
    next.delete('thread')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadParam])

  const railQuery = useQuery({
    queryKey: threadKeys.rail('open'),
    enabled: isLoaded,
    queryFn: () => fetchRail(getToken, 'open'),
    staleTime: 15_000,
  })
  const threads = railQuery.data?.threads ?? []
  // Full-list lookup so a closing thread still renders through the
  // evaporate animation (visibleRailThreads is render-only).
  const selected = threads.find((x) => x.id === selectedId) ?? null
  const visibleThreads = visibleRailThreads(threads, evaporatingId)

  // Auto-reopen an in-progress unfiled draft on the FIRST rail load —
  // in-render latch (prev-snapshot pattern), one-shot per mount; a
  // ?thread= deep-link wins because selectedId is already seeded.
  const [railSeen, setRailSeen] = useState(false)
  if (railQuery.data && !railSeen) {
    setRailSeen(true)
    if (!selectedId) {
      const draftId = pickAutoReopenThread(railQuery.data.threads)
      if (draftId) setSelectedId(draftId)
    }
  }

  const handleClose = async (id: string) => {
    const closed = await closeThread(getToken, id).then(
      () => true,
      () => false,
    )
    // Synchronous cache repair on success — keeps the auto-reopen latch
    // and Clear guard truthful while the evaporate animation plays (see
    // HomeV2 for the full failure story this guards against).
    if (closed) {
      await queryClient
        .cancelQueries({ queryKey: threadKeys.rail('open') })
        .catch(() => {})
      queryClient.setQueryData<{ threads: ThreadRailItem[] }>(
        threadKeys.rail('open'),
        (data) => markThreadClosedInRail(data, id),
      )
    }
    setSelectedId(null)
    setEvaporatingId(id)
  }

  // Clear — abandon an unfiled draft (single tap, recoverable from the
  // Threads archive). The guard keeps Clear away from filed threads.
  const handleClear = async (id: string) => {
    const target = threads.find((x) => x.id === id) ?? null
    if (!canClearThread(target)) return
    await handleClose(id)
  }

  if (selectedId) {
    return (
      <ThreadWorkspace
        threadId={selectedId}
        selected={selected}
        threads={visibleThreads}
        onBack={() => setSelectedId(null)}
        onSwitch={setSelectedId}
        onClose={handleClose}
        onClear={handleClear}
      />
    )
  }

  return (
    <TodayFeed
      threads={visibleThreads}
      hasThreads={threads.length > 0}
      loading={railQuery.isLoading}
      onOpen={setSelectedId}
      evaporatingId={evaporatingId}
      onEvaporated={() => {
        setEvaporatingId(null)
        queryClient.invalidateQueries({ queryKey: threadKeys.rail('open') })
      }}
    />
  )
}

function railLabel(thread: ThreadRailItem, newChat: string): string {
  if (thread.is_unfiled || !thread.customer_name) return thread.title ?? newChat
  return thread.customer_name
}

// ─────────────────────────────────────────────────────────────────────
// Feed — §4.5 header, needs-you stack, digest line, §4.10 thread rail.

function TodayFeed({
  threads,
  hasThreads,
  loading,
  onOpen,
  evaporatingId,
  onEvaporated,
}: {
  threads: ThreadRailItem[]
  // From the RAW rail list — the empty state must not depend on the
  // visibleRailThreads filter timing mid-evaporate (see HomeV2).
  hasThreads: boolean
  loading: boolean
  onOpen: (id: string) => void
  evaporatingId: string | null
  onEvaporated: () => void
}) {
  const { t } = useTranslation('today')
  const { getToken, isLoaded } = useAuth()
  const { organization } = useOrganization()
  const assistantName = useAssistantName()

  // "While you were working" — count of assistant requests since the
  // previous app session. Both bounds latch once per session (module
  // latches make the initializer idempotent under StrictMode). The
  // stamp is the window's upper bound so the mechanic's own
  // current-session requests never creep into the count when the query
  // refetches on window focus. No baseline → no fetch, no line.
  const [{ baseline, sessionStamp }] = useState(() => ({
    baseline: peekDigestBaseline(),
    sessionStamp: commitDigestStamp(),
  }))
  const historyQuery = useQuery({
    queryKey: ['eric-history', 'today-digest'],
    enabled: isLoaded && baseline !== null,
    staleTime: 5 * 60_000,
    queryFn: () =>
      authFetch<{ entries: DigestEntry[] }>(
        '/api/eric/history?limit=50',
        getToken,
      ),
  })
  const digestCount = countEntriesSince(
    historyQuery.data?.entries ?? [],
    baseline,
    sessionStamp,
  )

  // Evaporate on a timer so cleanup fires under reduced-motion too.
  useEffect(() => {
    if (!evaporatingId) return
    const tid = setTimeout(onEvaporated, 420)
    return () => clearTimeout(tid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaporatingId])

  return (
    <div className="mx-auto w-full max-w-[660px] space-y-6 lg:max-w-[720px]">
      {/* §4.5 page header: shop line above a balanced 22px title. */}
      <header className="space-y-0.5">
        {organization?.name ? (
          <div className="text-[13px] font-medium text-muted-foreground">
            {organization.name}
          </div>
        ) : null}
        <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
          {t('title')}
        </h1>
      </header>

      <NeedsYouStack />

      {digestCount > 0 ? (
        // The one-line digest deep-links to the full log (DESIGN.md §5
        // "Today carries only the one-line digest linking to it").
        <Link
          to="/activity"
          className="flex min-h-11 items-center gap-1 text-[13px] text-muted-foreground transition-[color,opacity] active:opacity-70 [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground"
        >
          <span className="min-w-0 truncate">
            {t('digest.line', { count: digestCount, name: assistantName })}
          </span>
          <ChevronRight aria-hidden="true" className="size-3.5 shrink-0" />
        </Link>
      ) : null}

      <section aria-label={t('threads.label')}>
        <h2 className="mb-2 text-[13px] font-semibold text-muted-foreground">
          {t('threads.label')}
        </h2>

        {loading ? (
          <div className="-mx-4 flex gap-3 overflow-hidden px-4">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-[76px] w-[178px] shrink-0 animate-pulse rounded-[14px] border border-border bg-muted/40 motion-reduce:animate-none"
              />
            ))}
          </div>
        ) : hasThreads ? (
          <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {threads.map((thread) => {
              // §4.10 thread card last line: the booked time when one
              // exists, else the last-activity moment.
              const lastLine = thread.appointment_at
                ? formatShortWhen(thread.appointment_at)
                : thread.last_message_at
                  ? formatRelative(thread.last_message_at)
                  : null
              return (
                <button
                  key={thread.id}
                  type="button"
                  onClick={() => onOpen(thread.id)}
                  className={`w-[178px] shrink-0 rounded-[14px] border border-border bg-card px-3.5 py-3 text-left shadow-1 transition-transform [transition-duration:120ms] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 ${
                    thread.id === evaporatingId
                      ? 'home-v2-evaporating pointer-events-none'
                      : ''
                  }`}
                >
                  <span className="block truncate text-[15px] font-[620] text-foreground">
                    {railLabel(thread, t('threads.new_chat'))}
                  </span>
                  {thread.vehicle_label ? (
                    <span className="mt-0.5 block truncate font-mono text-xs text-muted-foreground">
                      {thread.vehicle_label}
                    </span>
                  ) : null}
                  {lastLine ? (
                    <span className="mt-0.5 block truncate text-[13px] text-muted-foreground">
                      {lastLine}
                    </span>
                  ) : null}
                </button>
              )
            })}
          </div>
        ) : (
          <div className="rounded-[14px] border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
            {t('threads.empty', { name: assistantName })}
          </div>
        )}
      </section>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Workspace — one thread opened in place (behavior identical to HomeV2;
// the dock stays suppressed while the thread-scoped composer is up).

function ThreadWorkspace({
  threadId,
  selected,
  threads,
  onBack,
  onSwitch,
  onClose,
  onClear,
}: {
  threadId: string
  selected: ThreadRailItem | null
  threads: ThreadRailItem[]
  onBack: () => void
  onSwitch: (id: string) => void
  onClose: (id: string) => void
  onClear: (id: string) => void
}) {
  const { t } = useTranslation('today')
  const { suppressDock } = useShell()
  const name = selected?.customer_name ?? selected?.title ?? null
  const clearable = canClearThread(selected)

  // Suppression must land before paint or both composers flash a frame.
  useLayoutEffect(() => suppressDock(), [suppressDock])

  return (
    // data-surface is LOAD-BEARING: the --hv-* variables ThreadChat's
    // subtree still consumes (user bubbles, SlotPicker, RecordPanel) are
    // scoped to [data-surface='home-v2'] in index.css — dropping it
    // renders user bubbles white-on-transparent. Same precedent as
    // Threads.tsx. Dies with the hv-* family in the cleanup PR.
    <div
      data-surface="home-v2"
      className="mx-auto w-full max-w-[660px] space-y-4 lg:max-w-[720px]"
    >
      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          className="-ml-2 min-h-11 justify-start gap-1 px-2 text-muted-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:bg-transparent dark:hover:bg-transparent"
        >
          <ChevronLeft className="size-4" />
          {t('threads.back')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => (clearable ? onClear(threadId) : onClose(threadId))}
          className="min-h-11 justify-start px-2 text-muted-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:bg-transparent dark:hover:bg-transparent"
        >
          {clearable ? <X className="size-4" /> : <Check className="size-4" />}
          {clearable ? t('threads.clear') : t('threads.close')}
        </Button>
      </div>

      {threads.length > 1 ? (
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {threads.map((x) => {
            const active = x.id === threadId
            return (
              <Button
                key={x.id}
                type="button"
                variant="ghost"
                size="pill"
                onClick={() => onSwitch(x.id)}
                className={`shrink-0 gap-0 border ${
                  active
                    ? 'border-foreground bg-foreground text-background [@media(hover:hover)_and_(pointer:fine)]:hover:bg-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:text-background dark:hover:bg-foreground'
                    : 'border-border bg-card text-muted-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:bg-card [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground dark:hover:bg-card'
                }`}
              >
                <span className="max-w-[160px] truncate">
                  {railLabel(x, t('threads.new_chat'))}
                </span>
              </Button>
            )
          })}
        </div>
      ) : null}

      <ThreadChat key={threadId} threadId={threadId} customerName={name} />
    </div>
  )
}
